import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserProfile } from "../contexts/UserProfileContext";
import { getActiveProgramme } from "../services/api";
import { getWorkoutSettings } from "./useWorkoutSettings";
import { summariseTrainingHistory } from "../utils/trainingHistory";
import { nextPendingProgrammeSession } from "../utils/programmeContext";
import { weeklyMuscleSets } from "../utils/muscleVolume";
import { computeWeeklyTargets } from "../utils/weeklyTargets";
import {
  NEXT_STYLE,
  SELECTOR_STYLES,
  hasLoadEquipment,
  readLastArchetypeId,
  resolveMixedStyle,
} from "../utils/mixedStyle";

// Small seeded PRNG (mulberry32). The preview re-resolves whenever the form
// changes; a seeded draw keeps tie-breaks stable across re-renders, so the
// shown shape only changes when an input does or the user shuffles.
const seededRandom = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * Preview of what a selector (Surprise me / What's next) will build, resolved
 * before generation so the user sees — and can shuffle or hide — the shape
 * before spending a generation on it (#83).
 *
 * The dialog passes `preview.archetype.id` to generatePlan as the held shape,
 * so the session built is exactly the one shown.
 */
const useSessionShapePreview = ({
  planConfig,
  userProfile,
  isAuthenticated,
  open,
  // Off when the dialog is already generating a programme session.
  checkProgramme = true,
}) => {
  const { sessions } = useUserProfile();
  const [shuffledPast, setShuffledPast] = useState([]);
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 2 ** 31));
  const [pendingProgrammeSession, setPendingProgrammeSession] = useState(null);
  const lastArchetypeRef = useRef(null);

  const selection = planConfig?.workoutTypes;
  const selectionKey = JSON.stringify(selection || []);
  const normalised = (Array.isArray(selection) ? selection : [])
    .map((s) => String(s).toLowerCase());
  // No selection means "a session, any shape" — the resolver treats it as
  // Surprise me, so the preview should too.
  const selectorActive =
    normalised.length === 0 ||
    normalised.some((s) => SELECTOR_STYLES.includes(s));
  const isNext = normalised.includes(NEXT_STYLE);

  // Fresh rotation state each time the dialog opens or the selection changes.
  useEffect(() => {
    if (!open) return;
    lastArchetypeRef.current = readLastArchetypeId();
    setShuffledPast([]);
    setSeed(Math.floor(Math.random() * 2 ** 31));
  }, [open, selectionKey]);

  const history = useMemo(() => {
    if (!selectorActive || !isAuthenticated) return [];
    const { guidedSessionEnabled, historyLookbackDays } = getWorkoutSettings();
    const useGuidance =
      guidedSessionEnabled && planConfig?.useHistoryGuidance !== false;
    // What's next needs a full week to measure against, whatever the window.
    const lookbackDays = isNext
      ? Math.max(7, historyLookbackDays ?? 7)
      : historyLookbackDays;
    return useGuidance ? summariseTrainingHistory(sessions, { lookbackDays }) : [];
  }, [
    selectorActive,
    isAuthenticated,
    isNext,
    sessions,
    planConfig?.useHistoryGuidance,
  ]);

  // Only What's next names lagging muscles; skip the walk otherwise.
  const muscleSets = useMemo(
    () => (isNext && history.length ? weeklyMuscleSets(sessions) : null),
    [isNext, history, sessions],
  );

  // The goal-aware weekly targets What's next ranks against.
  const targets = useMemo(
    () =>
      computeWeeklyTargets({
        userProfile,
        trainingDates: (sessions || []).map((s) => s.started_at),
      }).counts,
    [userProfile, sessions],
  );

  const preview = useMemo(() => {
    if (!selectorActive) return null;
    return resolveMixedStyle(selection, {
      history,
      heldArchetypeId: planConfig?.mixedArchetypeId || null,
      excludeArchetypeIds: [lastArchetypeRef.current, ...shuffledPast],
      blockedArchetypeIds: userProfile?.excludedSessionShapes || [],
      sessionMinutes: planConfig?.sessionDuration,
      hasLoad: hasLoadEquipment(planConfig?.availableEquipment),
      readiness: planConfig?.readiness,
      preferences: {
        restStyle: userProfile?.restStyle,
        sessionPace: userProfile?.sessionPace,
      },
      muscleSets,
      noFaff: Boolean(planConfig?.noFaff),
      targets,
      random: seededRandom(seed),
    });
    // selection is covered by selectionKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectorActive,
    selectionKey,
    history,
    planConfig?.mixedArchetypeId,
    planConfig?.sessionDuration,
    planConfig?.availableEquipment,
    planConfig?.readiness,
    planConfig?.noFaff,
    userProfile?.excludedSessionShapes,
    userProfile?.restStyle,
    userProfile?.sessionPace,
    shuffledPast,
    seed,
    muscleSets,
    targets,
  ]);

  // Skip the shown shape (and any shuffled past) on the next resolve. Once
  // every option has been seen, start the cycle again rather than stalling.
  const shuffle = useCallback(() => {
    const currentId = preview?.archetype?.id;
    if (!currentId) return;
    setShuffledPast((prev) =>
      prev.includes(currentId) ? [currentId] : [...prev, currentId],
    );
    setSeed((s) => s + 1);
  }, [preview]);

  // What's next defers to an active programme: if a planned session is
  // waiting, that is what's next, and the dialog says so.
  useEffect(() => {
    if (!open || !isNext || !isAuthenticated || !checkProgramme) {
      setPendingProgrammeSession(null);
      return undefined;
    }
    let cancelled = false;
    getActiveProgramme()
      .then((programme) => {
        if (!cancelled) {
          setPendingProgrammeSession(nextPendingProgrammeSession(programme));
        }
      })
      .catch(() => {
        if (!cancelled) setPendingProgrammeSession(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isNext, isAuthenticated, checkProgramme]);

  return { preview, shuffle, pendingProgrammeSession };
};

export default useSessionShapePreview;
